import amplitude from 'amplitude-js';
import React from 'react';
import { FaRegCheckSquare, FaRegTimesCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { GQL_ENDPOINT } from './constants';
import moment from 'moment';

const toastError = (str, autoHide = true) => {
  const conf = {};
  if (autoHide) {
    conf.autoClose = 3000;
  }
  toast.error(
    <span style={{ color: 'white' }}>
      <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
        <FaRegTimesCircle />
      </span>
      {` ${str}`}
    </span>,
    conf
  );
};

export const displayError = errorObjOrString => {
  if (typeof errorObjOrString === 'string') {
    toastError(errorObjOrString);
    return;
  }
  const { graphQLErrors, networkError, message } = errorObjOrString;
  let displayMessage = message;
  if (graphQLErrors && graphQLErrors.length > 0) {
    displayMessage = graphQLErrors[0].message;
  } else if (networkError) {
    displayMessage = networkError.message;
  }
  toastError(displayMessage);
};

export const getGraphQLErrString = ({ graphQLErrors, networkError, message }) =>
  graphQLErrors && graphQLErrors.length > 0
    ? graphQLErrors[0].message
    : networkError
    ? networkError.message
    : message;

export const displaySuccess = (str, autoHide = true) => {
  const conf = {};
  if (autoHide) {
    conf.autoClose = 3000;
  }
  toast.success(
    <span style={{ color: 'white' }}>
      <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
        <FaRegCheckSquare />
      </span>
      {` ${str}`}
    </span>,
    conf
  );
};
export const displayInfo = str => toast.info(str);

export const getFullName = (firstName, lastName) => {
  const fname = firstName || '';
  const lname = lastName || '';
  return `${fname} ${lname}`.trim();
};

export const isImageBigEnough = file =>
  new Promise(resolve => {
    if (!file) {
      return false;
    }
    //eslint-disable-next-line
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function(e) {
      //eslint-disable-next-line
      const image = new Image();
      image.src = e.target.result;
      image.onload = function() {
        const height = this.height;
        const width = this.width;
        // console.log(`h: ${height} w: ${width}`);
        if (height < 500 || width < 500) {
          resolve(false);
          return;
        }
        resolve(true);
        return;
      };
    };
  });

export const getTransformedImagesFromCloudinaryResponse = json => {
  const dp300 = {};
  const eager0 = json.eager[0];
  dp300.url = eager0.secure_url;
  dp300.height = eager0.height;
  dp300.width = eager0.width;

  return { dp300 };
};

const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const isValidEmail = email => {
  return EMAIL_REGEX.test(email);
};

export const setDefaultHospitalId = hospitalId =>
  localStorage.setItem('DEFAULT_HOSPITAL_ID', hospitalId);

export const getDefaultHospitalId = () =>
  localStorage.getItem('DEFAULT_HOSPITAL_ID');

export const SALUTATION_REGEX = /(^(dr|mr|miss|ms|mrs)[.]*\s+){0,1}(.*)/i;

export const getNameWithoutSalutation = (name: string) => {
  if (!name) {
    return name;
  }
  const match = `${name}`.trim().match(SALUTATION_REGEX);
  return !match ? `${name}`.trim() : match[3];
};

export const getFirstNameWithoutSalutation = (name: string) => {
  return getNameWithoutSalutation(name).split(/\s+/)[0];
};

export const getFirstNameUpperCase = (name: string) =>
  getFirstNameWithoutSalutation(name).toUpperCase();

export function betterParseInt(value: string) {
  if (/^[-+]?(\d+|Infinity)$/.test(value)) {
    return Number(value);
  } else {
    return NaN;
  }
}

export const amplitudeInit = () =>
  amplitude.getInstance().init('1e8a67f9fe98ca0f56e167581a9ec6a0');

export const setAmplitudeUser = userId =>
  amplitude.getInstance().setUserId(userId);

export const logAmplitudeEvent = (
  eventType,
  // eslint-disable-next-line no-unused-vars
  eventProp = {},
  // eslint-disable-next-line no-unused-vars
  appendLoginUser
) => {};

let hospitalCache = null;
let fetchPromise = null;

export const getHospitalSiteCode = async () => {
  if (hospitalCache) {
    return hospitalCache;
  }

  // If a fetch is already in progress, wait for it to complete
  if (fetchPromise) {
    return fetchPromise;
  }

  fetchPromise = (async () => {
    const url = GQL_ENDPOINT;
    const req_body = {
      query: `{ getHospitals(showAll : true){
        id
        name
        siteCode
      }}`
    };

    const fetchOpts = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(req_body)
    };

    const response = await fetch(url, fetchOpts);
    const result = await response.json();
    hospitalCache = result;
    fetchPromise = null; // Reset the fetchPromise once done
    return result;
  })();

  return fetchPromise;
};

export const getHospitalById = id => {
  const url = GQL_ENDPOINT;
  const req_body = {
    query: `{ getHospitalById(id : ${parseInt(id)}){
      id
      siteCode
      name
      cityId
      cityName }}`
  };

  const fetchOpts = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify(req_body)
  };

  return fetch(url, fetchOpts).then(r => r.json());
};

export const getDoctorRankings = hospitalId => {
  const url = GQL_ENDPOINT;

  const req_body = {
    query: `query getDoctorsRanking($input: RankingSearchInput){
      getDoctorsRanking(input: $input){
        ranking
        doctorId
        doctorName
        primarySpecialty
        secondarySpecialty
        department
        dateSincePracticing
      }
    }
    `,
    variables: {
      input: {
        hospId: hospitalId
      }
    }
  };

  const fetchOpts = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify(req_body)
  };

  return fetch(url, fetchOpts)
    .then(response => {
      return response.json();
    })
    .catch(err => {
      return err;
    });
};

export const trimisterOptions = [
  { label: 'Trimester 1', value: '1' },
  { label: 'Trimester 2', value: '2' },
  { label: 'Trimester 3', value: '3' }
];

export const scanTypeOptions = [
  { label: 'Pregnancy', value: 'Pregnancy' },
  { label: 'Pregnancy procedures', value: 'Pregnancy procedures' },
  { label: 'Gynae', value: 'Gynae' },
  { label: 'Others', value: 'Others' }
];

export const pregnancyWeekOptions = [
  { label: '0-12 weeks', value: '0-12 weeks' },
  { label: '14-17 week', value: '14-17 week' },
  { label: '18-21 week', value: '18-21 week' },
  { label: '20-24 week', value: '20-24 week' },
  { label: '28+ weeks', value: '28+ weeks' }
];

export const commonCouponFormData = {
  selectedLtItemId: '',
  selectedSiteCode: '',
  selectedStartDate: moment(new Date()).format('YYYY-MM-DD'),
  selectedExpiryDate: moment()
    .add(1, 'years')
    .format('YYYY-MM-DD'),
  enteredComment: ''
};

export const recommendationTypeOptions = [
  { label: 'Recommended', value: 'Recommended' },
  { label: 'Prescribed', value: 'Prescribed' }
];

export const targetTypeOptions = [
  { value: 'ALL', label: 'All' },
  { value: 'ADMIN USER', label: 'Admin users' },
  { value: 'BOOKING', label: 'Bookings' },
  { value: 'DOCTOR PROFILE', label: 'Doctor Profile' },
  { value: 'HOSPITAL', label: 'Hospitals' },
  { value: 'MODIFY_SCHEDULE', label: 'Schedule' },
  { value: 'SLOT', label: 'Slots' },
  { value: 'VACATION', label: 'Vacation' },
  { value: 'SPECIALTY_MAPPING', label: 'Specialty Mapping' }
];

export const actionOptions = [
  { value: 'ALL', label: 'All' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Created', label: 'Created' },
  { value: 'Deleted', label: 'Deleted' },
  { value: 'Delay Added', label: 'Delay Added' },
  { value: 'Modified', label: 'Modified' },
  { value: 'Reserved', label: 'Reserved' },
  { value: 'Schedule Blocked', label: 'Schedule Blocked' },
  { value: 'Schedule Unblocked', label: 'Schedule Unblocked' },
  { value: 'Timings changed', label: 'Timings changed' },
  { value: 'Unreserved', label: 'Unreserved' },
  { value: 'Updated', label: 'Updated' },
  { value: 'Vacation Approved', label: 'Vacation Approved' },
  { value: 'Vacation Rejected', label: 'Vacation Rejected' },
  { value: 'Reordered', label: 'Reordered' }
];

export const doneByOptions = [
  { value: 'ALL', label: 'All' },
  { value: 'CX App', label: 'CX App' },
  { value: 'HIMS Support', label: 'HIMS Support' },
  { value: 'IT Support', label: 'IT Support' },
  { value: 'Practo', label: 'Practo' },
  { value: 'SysBot Beep Beep', label: 'SysBot Beep Beep' },
  { value: 'Users Service', label: 'Users Service' },
  { value: 'Visit Health', label: 'Visit Health' }
];

export const isAge18orAbove = dob => {
  if (!dob) return false;
  return moment().diff(moment(dob), 'years') >= 18;
};
